'use client'

import React, { Component, ReactNode } from 'react'
import posthog from 'posthog-js'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: React.ErrorInfo
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo
    })

    // Track error in PostHog
    if (typeof window !== 'undefined' && posthog) {
      posthog.capture('react_error_boundary', {
        error_name: error.name,
        error_message: error.message,
        error_stack: error.stack,
        component_stack: errorInfo.componentStack,
        user_agent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        props: JSON.stringify(this.props, null, 2)
      })
    }

    console.error('Error caught by boundary:', error, errorInfo)
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined })

    // Track retry attempt
    if (typeof window !== 'undefined' && posthog) {
      posthog.capture('error_boundary_retry', {
        url: window.location.href,
        timestamp: new Date().toISOString()
      })
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="max-w-md w-full mx-auto p-6">
              <div className="text-center">
                <h1 className="text-2xl font-bold text-foreground mb-4">
                  Something went wrong
                </h1>
                <p className="text-muted-foreground mb-6">
                  We&apos;re sorry, but something unexpected happened. The error has been logged and we&apos;ll look into it.
                </p>
                <div className="space-y-3">
                  <button
                    onClick={this.handleRetry}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md transition-colors"
                  >
                    Try again
                  </button>
                  <button
                    onClick={() => window.location.reload()}
                    className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/80 px-4 py-2 rounded-md transition-colors"
                  >
                    Reload page
                  </button>
                </div>
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <details className="mt-6 text-left">
                    <summary className="cursor-pointer text-sm text-muted-foreground">
                      Error details (development only)
                    </summary>
                    <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto">
                      {this.state.error.stack}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          </div>
        )
      )
    }

    return this.props.children
  }
}